'use client';

import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useState, useEffect, useCallback } from 'react';

import ChevronIcon from '@/fo/icons/base/icons/chevron24.svg';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitLabelBase } from '@/fo/ui/kit/label/base';
import { UiKitLightboxBase } from '@/fo/ui/kit/lightbox/base';
import { useMediaQuery } from '@/fo/ui/mod/media/query';
import { utilEventConfigurableProduct } from '@/fo/util/event-configurable-product';
import { staticMediaUrl } from '@/fo/util/image-loader';
import {
  getProductGalleryAlt,
  getProductH1,
  getProductThumbAlt,
  MediaGallery,
  Label
} from '@/fo/util/map-product-data';

import {
  PrevButton,
  NextButton,
  usePrevNextButtons
} from '../components/Arrows/Arrows';
import { Thumb } from '../components/Thumb/Thumb';

import { styles } from './UiProductPictureMain.styles';

const cursorUrl = staticMediaUrl('icons/plus-circle.svg');

export interface UiProductPictureMainProps {
  className?: string;
  images: MediaGallery;
  tags?: Label[];
  productH1: string;
}

export function UiProductPictureMain({
  className,
  images: initialImages,
  tags: initialTags,
  productH1: initialProductH1
}: UiProductPictureMainProps) {
  const t = useTranslations('product.alt');
  const slots = styles();
  const isDesktop = useMediaQuery('sm');

  const [images, setImages] = useState(initialImages);
  const [tags, setTags] = useState(initialTags);
  const [productH1, setProductH1] = useState(initialProductH1);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [prevArrowVisible, setPrevArrowVisible] = useState(true);
  const [nextArrowVisible, setNextArrowVisible] = useState(true);
  const [emblaRef, emblaApi] = useEmblaCarousel();
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    axis: isDesktop ? 'y' : 'x'
  });
  const { onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi);

  const onThumbClick = (index: number) => {
    if (!emblaApi) return;

    emblaApi.scrollTo(index);
  };

  const onSelect = useCallback(() => {
    if (!emblaApi || !emblaThumbsApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());

    emblaThumbsApi.scrollTo(emblaApi.selectedScrollSnap());

    setPrevArrowVisible(emblaApi.canScrollPrev());
    setNextArrowVisible(emblaApi.canScrollNext());
  }, [emblaApi, emblaThumbsApi]);

  useEffect(() => {
    return utilEventConfigurableProduct.onProductSelect((product) => {
      const { mediaGallery, labels } = product;

      setImages(mediaGallery);
      setTags(labels);
      setProductH1(getProductH1(product));
    });
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();

    emblaApi.on('select', onSelect).on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  return (
    <div className={slots.base({ className })}>
      {tags && (
        <div className={slots.tags()}>
          {tags.map(({ name, color }) => (
            <UiKitLabelBase
              key={name}
              customColor={color}
            >
              {name}
            </UiKitLabelBase>
          ))}
        </div>
      )}
      {images.length > 1 && (
        <div className={slots.thumbs()}>
          <UiKitButtonBase
            onClick={() => emblaApi?.scrollPrev()}
            className={slots.thumbArrow({
              className: '-rotate-90',
              hide: !prevArrowVisible
            })}
            rounded="full"
            color="outlined"
          >
            <ChevronIcon />
          </UiKitButtonBase>

          <div
            ref={emblaThumbsRef}
            className={slots.thumbsViewport()}
          >
            <div className={slots.thumbsContainer()}>
              {images.map((image, index) => (
                <Thumb
                  key={index}
                  onClick={() => onThumbClick(index)}
                  selected={index === selectedIndex}
                >
                  {image.type === 'video' && (
                    <video
                      className="h-25 w-25 object-cover"
                      width={100}
                      height={100}
                    >
                      <source
                        src={image.src}
                        type="video/mp4"
                      />
                    </video>
                  )}
                  {image.type === 'image' && (
                    <Image
                      className={slots.image()}
                      src={image.src}
                      alt={getProductThumbAlt(productH1, index, t('thumbnail'))}
                      width={100}
                      height={100}
                    />
                  )}
                </Thumb>
              ))}
            </div>
          </div>

          <UiKitButtonBase
            onClick={() => emblaApi?.scrollNext()}
            className={slots.thumbArrow({
              className: 'h-6 rotate-90',
              hide: !nextArrowVisible
            })}
            rounded="full"
            color="outlined"
          >
            <ChevronIcon />
          </UiKitButtonBase>
        </div>
      )}
      <div
        className={slots.viewport()}
        ref={emblaRef}
      >
        <div className={slots.container()}>
          {images.map((image, index) => (
            <div
              className={slots.imageWrapper()}
              key={image.src}
              onClick={() => setIsOpen(!isOpen)}
              style={{
                cursor: `url(${cursorUrl}), pointer`
              }}
            >
              {image.type === 'video' && (
                <video
                  className="h-165 w-165 object-cover"
                  autoPlay
                  muted
                  width={660}
                  height={660}
                >
                  <source
                    src={image.src}
                    type="video/mp4"
                  />
                </video>
              )}
              {image.type === 'image' && (
                <Image
                  className={slots.image()}
                  src={image.src}
                  alt={getProductGalleryAlt(productH1, index, t('photo'))}
                  width={660}
                  height={660}
                  priority={index === 0}
                />
              )}
            </div>
          ))}
        </div>

        {images.length > 1 && (
          <>
            <PrevButton
              className={slots.arrow({ hide: !prevArrowVisible })}
              onClick={onPrevButtonClick}
            />
            <NextButton
              className={slots.arrow({ hide: !nextArrowVisible })}
              onClick={onNextButtonClick}
            />
          </>
        )}
      </div>
      {isOpen && (
        <UiKitLightboxBase
          images={images}
          open={isOpen}
          onOpenChange={() => setIsOpen(!isOpen)}
          initialIndexSlide={selectedIndex}
        />
      )}
    </div>
  );
}
